import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { requestContactsList } from '../asyncHelpers/contactsService';
import {
    CONTACTS_DEFAULT_PAGE,
    CONTACTS_DEFAULT_SORT,
    DEFAULT_LIST_ID,
    DEFAULT_VIEW_ID,
    GRID_DEFAULT_PAGE_SIZE,
} from '../sic-lib/defaults';
import { ContactsListSortDirection } from '../sic-lib/enums/ContactsListSortDirection';
import { RequestStatus } from '../sic-lib/enums/RequestStatus';
import { Contact } from '../sic-lib/types/Contact';
import { ContactListFilterItem } from '../sic-lib/types/ContactListFilterItem';
import { ContactListSortItem } from '../sic-lib/types/ContactListSortItem';
import { hasDistinctLeadId } from '../sic-lib/utils/functionalFilters';
import { sanitizeAllTags } from '../sic-lib/utils/sanitizeHtml';
import { RootState } from './store';

/*
 *  Conversation Grid Reducer
 *  ---------------------------
 *
 *  Holds the state of the Conversation Grid page. When new contacts are requested they are mapped
 *  to the view assigned to the list of contacts requested.
 *
 *  TIP: There is a possible overlap in contact records between this reducer and the reducer
 *  which caches Conversation Grid state. Though it is against Redux best practices to hold
 *  two copies of the same object; this decision was made to simplify the complex ContactDisplay
 *  component tree and to possibly minimize the number of requests for selected contacts.
 *
 *  IMPORTANT: When running updates on contact record be sure to run an update for any duplicate
 *  contact record that might be in the Conversation Grid reducer.
 */

// --------------
// STATE

export interface ConversationGridState {
    requestStatus: RequestStatus;
    contacts: Contact[];
    contactCount: number;
    page: number;
    pageCount: number;
    searchKeyword: string;
    sortField: ContactListSortItem;
    filters: ContactListFilterItem[];
    selectAll: boolean;
    selected: number[];
    listId: number;
    /*
       TIP: viewId, viewIdLabels should not be set manually. They are reference
       only and set from list request response.
     */
    viewId: number;
}

const initialState: ConversationGridState = {
    requestStatus: RequestStatus.none,
    contacts: [],
    contactCount: 0,
    page: CONTACTS_DEFAULT_PAGE,
    pageCount: 0,
    searchKeyword: '',
    sortField: CONTACTS_DEFAULT_SORT,
    filters: [],
    selectAll: false,
    selected: [],
    listId: DEFAULT_LIST_ID,
    viewId: DEFAULT_VIEW_ID,
};

const conversationGridSlice = createSlice({
    name: 'conversationGrid',
    initialState,
    reducers: {
        setListId: (state, action: PayloadAction<{ listId: number }>) => {
            const { listId } = action.payload;
            state.listId = listId;
            state.filters = [];
            state.page = CONTACTS_DEFAULT_PAGE;
            state.searchKeyword = '';
            state.selectAll = false;
            state.selected = [];
            state.sortField = CONTACTS_DEFAULT_SORT;
        },
        addContacts: (state, action: PayloadAction<{ contacts: Contact[] }>) => {
            const { contacts } = action.payload;
            state.contacts = [...contacts, ...state.contacts];
        },
        removeContact: (state, action: PayloadAction<{ contactId: number | number[] }>) => {
            const { contactId } = action.payload;
            state.contacts = state.contacts.filter(hasDistinctLeadId(contactId));
        },
        setSort: (
            state,
            action: PayloadAction<{ sortField: number; sortDirection: ContactsListSortDirection }>,
        ) => {
            const { sortField, sortDirection } = action.payload;
            state.sortField = {
                fieldId: sortField,
                direction: sortDirection,
            };
            state.page = CONTACTS_DEFAULT_PAGE;
        },
        setSearch: (state, action: PayloadAction<{ searchKeyword: string }>) => {
            const { searchKeyword } = action.payload;
            state.searchKeyword = searchKeyword;
            state.page = CONTACTS_DEFAULT_PAGE;
        },
        setPage: (state, action: PayloadAction<{ page: number }>) => {
            const { page } = action.payload;
            state.page = page;
        },
        setFilters: (state, action: PayloadAction<{ filters: ContactListFilterItem[] }>) => {
            const { filters } = action.payload;
            state.filters = filters;
            state.page = CONTACTS_DEFAULT_PAGE;
        },
        selectAllContacts: (state, action: PayloadAction<{ selectStatus: boolean }>) => {
            const { selectStatus } = action.payload;
            state.selectAll = selectStatus;
            state.selected = [];
        },
        selectContact: (
            state,
            action: PayloadAction<{ contactId: number; selectStatus: boolean }>,
        ) => {
            const { contactId, selectStatus } = action.payload;
            state.selected = !state.selectAll
                ? selectStatus
                    ? [...state.selected, contactId]
                    : state.selected.filter(itm => itm !== contactId)
                : selectStatus
                ? state.selected.filter(itm => itm !== contactId)
                : [...state.selected, contactId];
        },
        clearSelected: state => {
            state.selectAll = false;
            state.selected = [];
        },
        updateContactName: (
            state,
            action: PayloadAction<{ contactId: number; firstName: string; lastName: string }>,
        ) => {
            const { contactId, firstName, lastName } = action.payload;
            state.contacts = state.contacts.map(itm => {
                if (itm.contactId === contactId) {
                    itm = {
                        ...itm,
                        firstName: firstName,
                        lastName: lastName,
                    };
                }
                return itm;
            });
        },
        // importContacts: (state, action: PayloadAction<{ merge: JSON }>) => {
        //     const { merge } = action.payload;
        // },
    },
    extraReducers: builder => {
        builder
            .addCase(requestContacts.pending, (state, action) => {
                state.requestStatus = RequestStatus.active;
            })
            .addCase(requestContacts.fulfilled, (state, action) => {
                if (action.payload) {
                    state.requestStatus = RequestStatus.success;
                    state.contacts = action.payload.contactList;
                    state.contactCount = action.payload.totalContacts;
                    state.filters = action.payload.filters;
                    state.pageCount = action.payload.totalPages;
                    state.viewId =
                        action.payload.viewId !== null ? action.payload.viewId : DEFAULT_VIEW_ID;
                }
            })
            .addCase(requestContacts.rejected, (state, action) => {
                state.requestStatus = RequestStatus.error;
                state.contacts = [];
                state.contactCount = 0;
            });
    },
});

export const {
    setListId,
    addContacts,
    removeContact,
    setSort,
    setSearch,
    setPage,
    setFilters,
    selectAllContacts,
    selectContact,
    clearSelected,
    updateContactName,
    // importContacts,
} = conversationGridSlice.actions;

export default conversationGridSlice.reducer;

export const requestContacts = createAsyncThunk(
    'conversationGrid/requestContacts',
    async (arg, { getState }) => {
        const appState = getState() as RootState;
        if (appState.conversationGrid) {
            const { data } = await requestContactsList({
                listId: appState.conversationGrid.listId,
                filters: appState.conversationGrid.filters,
                pageNum: appState.conversationGrid.page,
                pageSize: GRID_DEFAULT_PAGE_SIZE,
                searchKeyword: sanitizeAllTags(appState.conversationGrid.searchKeyword),
                sort: appState.conversationGrid.sortField,
            });
            return data;
        }
    },
);

// ---------------
// ACTIONS

// /* Request Contacts */
// interface RequestGridContactsSuccessAction {
//     type: 'REQUEST_GRID_CONTACTS';
//     requestStatus: RequestStatus.active;
// }

// interface ReceiveGridContactsErrorAction {
//     type: 'RECEIVE_GRID_CONTACTS_ERROR';
//     requestStatus: RequestStatus.error;
//     contacts: Contact[];
//     contactCount: number;
// }

// interface ReceiveGridContactsSuccessAction {
//     type: 'RECEIVE_GRID_CONTACTS_SUCCESS';
//     requestStatus: RequestStatus.success;
//     contacts: Contact[];
//     contactCount: number;
//     filters: ContactListFilterItem[];
//     pageCount: number;
//     viewId: number;
// }

// interface SetGridListIdAction {
//     /*
//         TIP: when requesting a new list of contacts all values should be reset to default
//         before being used in a request. This is called from the CnversationGridPage component
//         whenever the react-router listId url param has been changed.
//      */
//     type: 'SET_GRID_LIST_ID';
//     listId: number;
//     filters: ContactListFilterItem[];
//     page: number;
//     search: string;
//     sort: typeof CONTACTS_DEFAULT_SORT;
//     selectAll: false;
//     selected: number[];
// }

// /* Contact Operations */
// interface AddGridContactAction {
//     type: 'ADD_GRID_CONTACT';
//     contacts: Contact[];
// }

// interface RemoveGridContactsAction {
//     type: 'REMOVE_GRID_CONTACTS';
//     contactId: number | number[];
// }

// /* Contacts Filter Actions */
// interface SetGridSortAction {
//     type: 'SET_GRID_SORT';
//     sortField: number;
//     sortDirection: ContactsListSortDirection;
//     page: typeof CONTACTS_DEFAULT_PAGE;
// }

// interface SetGridSearchAction {
//     type: 'SET_GRID_SEARCH_KEYWORD';
//     searchKeyword: string;
//     page: typeof CONTACTS_DEFAULT_PAGE;
// }

// interface SetGridPageAction {
//     type: 'SET_GRID_PAGE';
//     page: number;
// }

// interface SetGridFiltersAction {
//     type: 'SET_GRID_FILTERS';
//     filters: ContactListFilterItem[];
//     page: typeof CONTACTS_DEFAULT_PAGE;
// }

// interface SelectAllGridContactsAction {
//     type: 'SELECT_ALL_GRID_CONTACTS';
//     selectStatus: boolean;
// }

// interface SelectGridContactAction {
//     type: 'SELECTED_GRID_CONTACT';
//     contactId: number;
//     selectStatus: boolean;
// }

// interface ClearSelectedGridContactsAction {
//     type: 'CLEAR_SELECTED_GRID_CONTACTS';
// }

// interface UpdateContactName {
//     type: 'UPDATE_CONTACT_NAME';
//     contactId: number;
//     firstName: string;
//     lastName: string;
// }

// //~ CONTACTS IMPORT
// //~ --------------------
// interface ImportContactsAction {
//     type: 'IMPORT_CONTACTS';
//     merge: JSON;
// }

// export type ContactActions =
//     | RequestGridContactsSuccessAction
//     | ReceiveGridContactsErrorAction
//     | ReceiveGridContactsSuccessAction
//     | SetGridListIdAction
//     | AddGridContactAction
//     | RemoveGridContactsAction
//     | SetGridPageAction
//     | SetGridSortAction
//     | SetGridSearchAction
//     | SetGridFiltersAction
//     | SelectAllGridContactsAction
//     | SelectGridContactAction
//     | ClearSelectedGridContactsAction
//     | UpdateContactName;

// // ---------------
// // ACTION CREATORS

// export const actionCreators = {
//     /* Request Grid Contacts */
//     requestContacts: (): AppThunkAction<ContactActions> => async (dispatch, getState) => {
//         const appState = getState();
//         if (appState.conversationGrid) {
//             try {
//                 dispatch({
//                     type: 'REQUEST_GRID_CONTACTS',
//                     requestStatus: RequestStatus.active,
//                 } as RequestGridContactsSuccessAction);

//                 const { data } = await requestContactsList({
//                     listId: appState.conversationGrid.listId,
//                     filters: appState.conversationGrid.filters,
//                     pageNum: appState.conversationGrid.page,
//                     pageSize: GRID_DEFAULT_PAGE_SIZE,
//                     searchKeyword: sanitizeAllTags(appState.conversationGrid.searchKeyword),
//                     sort: appState.conversationGrid.sortField,
//                 });
//                 // console.log("Filters Request", data.filters)
//                 return dispatch({
//                     type: 'RECEIVE_GRID_CONTACTS_SUCCESS',
//                     requestStatus: RequestStatus.success,
//                     contacts: data.contactList,
//                     contactCount: data.totalContacts,
//                     filters: data.filters,
//                     pageCount: data.totalPages,
//                     viewId: data.viewId !== null ? data.viewId : DEFAULT_VIEW_ID,
//                 });
//             } catch (e: any) {
//                 if (!axios.isCancel(e)) {
//                     return dispatch({
//                         type: 'RECEIVE_GRID_CONTACTS_ERROR',
//                         requestStatus: RequestStatus.error,
//                         contacts: [],
//                         contactCount: 0,
//                     });
//                 }
//             }
//         }
//     },
//     /**
//      * setListId
//      *
//      * @description
//      * Sets the listId which will be requested by the system, acts as a reset-all
//      * for "list-session" settings such as page, selected contacts, search, etc.
//      * (prepares the state for selecting a list with no settings carried over from the
//      * work done on the previous list)
//      */
//     setListId: (listId: number) => {
//         return {
//             type: 'SET_GRID_LIST_ID',
//             listId,
//             filters: [],
//             page: CONTACTS_DEFAULT_PAGE,
//             search: '',
//             selectAll: false,
//             selected: [],
//             sort: CONTACTS_DEFAULT_SORT,
//         } as SetGridListIdAction;
//     },
//     /* Grid Contact Operations */
//     addContact: (contacts: Contact[]) => {
//         return {
//             type: 'ADD_GRID_CONTACT',
//             contacts: contacts,
//         } as AddGridContactAction;
//     },
//     removeContact: (contactId: number | number[]) => {
//         return {
//             type: 'REMOVE_GRID_CONTACTS',
//             contactId,
//         } as RemoveGridContactsAction;
//     },
//     /* Grid Filter Actions */
//     setSort: (fieldId: number, sortDirection: ContactsListSortDirection) => {
//         return {
//             type: 'SET_GRID_SORT',
//             sortField: fieldId,
//             sortDirection,
//             page: CONTACTS_DEFAULT_PAGE,
//         } as SetGridSortAction;
//     },
//     setSearch: (searchKeyword: string) => {
//         return {
//             type: 'SET_GRID_SEARCH_KEYWORD',
//             searchKeyword,
//             page: CONTACTS_DEFAULT_PAGE,
//         } as SetGridSearchAction;
//     },
//     setPage: (page: number) => {
//         return {
//             type: 'SET_GRID_PAGE',
//             page,
//         } as SetGridPageAction;
//     },
//     setFilters: (filters: ContactListFilterItem[]) => {
//         return {
//             type: 'SET_GRID_FILTERS',
//             filters,
//             page: CONTACTS_DEFAULT_PAGE,
//         } as SetGridFiltersAction;
//     },
//     /* Select Grid Contact Actions */
//     selectAllContacts: (selectStatus: boolean) => {
//         return {
//             type: 'SELECT_ALL_GRID_CONTACTS',
//             selectStatus,
//         } as SelectAllGridContactsAction;
//     },
//     selectContact: (contactId: number, selectStatus: boolean) => {
//         return {
//             type: 'SELECTED_GRID_CONTACT',
//             contactId,
//             selectStatus,
//         } as SelectGridContactAction;
//     },
//     clearSelected: () => {
//         return {
//             type: 'CLEAR_SELECTED_GRID_CONTACTS',
//         } as ClearSelectedGridContactsAction;
//     },
//     updateContactName: (contactId: number, firstName: string, lastName: string) => {
//         return {
//             type: 'UPDATE_CONTACT_NAME',
//             contactId,
//             firstName,
//             lastName,
//         } as UpdateContactName;
//     },
//     //~ CONTACT IMPORT STATE ACTIONS
//     //~ ----------------------------------
//     importContacts: (merge: JSON) => {
//         return {
//             type: 'IMPORT_CONTACTS',
//             merge,
//         } as ImportContactsAction;
//     },
// };

// // ---------------
// // REDUCERS

// export const reducer = (
//     state: ConversationGridState | undefined = { ...initialState },
//     action: ContactActions,
// ): ConversationGridState => {
//     switch (action.type) {
//         /* Request Contacts */
//         case 'REQUEST_GRID_CONTACTS':
//             return {
//                 ...state,
//                 ...{
//                     requestStatus: action.requestStatus,
//                 },
//             };
//         case 'RECEIVE_GRID_CONTACTS_ERROR':
//             return {
//                 ...state,
//                 ...{
//                     requestStatus: action.requestStatus,
//                     contacts: action.contacts,
//                     contactCount: action.contactCount,
//                 },
//             };
//         case 'RECEIVE_GRID_CONTACTS_SUCCESS':
//             return {
//                 ...state,
//                 ...{
//                     requestStatus: action.requestStatus,
//                     contacts: action.contacts,
//                     contactCount: action.contactCount,
//                     filters: action.filters,
//                     pageCount: action.pageCount,
//                     viewId: action.viewId,
//                 },
//             };
//         /* Grid Low-Level State Ops */
//         case 'SET_GRID_LIST_ID':
//             return {
//                 ...state,
//                 ...{
//                     listId: action.listId,
//                     filters: action.filters,
//                     page: action.page,
//                     searchKeyword: action.search,
//                     selectAll: action.selectAll,
//                     selected: action.selected,
//                     sortField: action.sort,
//                 },
//             };
//         /* Contact Operations */
//         case 'ADD_GRID_CONTACT':
//             return {
//                 ...state,
//                 ...{
//                     contacts: [...action.contacts, ...state.contacts],
//                 },
//             };
//         case 'REMOVE_GRID_CONTACTS':
//             return {
//                 ...state,
//                 ...{
//                     contacts: state.contacts.filter(hasDistinctLeadId(action.contactId)),
//                 },
//             };
//         /* Contacts Filter Actions */
//         case 'SET_GRID_SORT':
//             return {
//                 ...state,
//                 ...{
//                     sortField: {
//                         fieldId: action.sortField,
//                         direction: action.sortDirection,
//                     },
//                     page: action.page,
//                 },
//             };
//         case 'SET_GRID_SEARCH_KEYWORD':
//             return {
//                 ...state,
//                 ...{
//                     searchKeyword: action.searchKeyword,
//                     page: action.page,
//                 },
//             };
//         case 'SET_GRID_PAGE':
//             return {
//                 ...state,
//                 ...{
//                     page: action.page,
//                 },
//             };
//         case 'SET_GRID_FILTERS':
//             return {
//                 ...state,
//                 ...{
//                     filters: action.filters,
//                     page: action.page,
//                 },
//             };
//         /* Select Grid Contact Actions */
//         case 'SELECT_ALL_GRID_CONTACTS':
//             return {
//                 ...state,
//                 ...{
//                     selectAll: action.selectStatus,
//                     selected: [],
//                 },
//             };
//         case 'SELECTED_GRID_CONTACT':
//             return {
//                 ...state,
//                 ...{
//                     selected: !state.selectAll
//                         ? action.selectStatus
//                             ? [...state.selected, action.contactId]
//                             : state.selected.filter(itm => itm !== action.contactId)
//                         : action.selectStatus
//                         ? state.selected.filter(itm => itm !== action.contactId)
//                         : [...state.selected, action.contactId],
//                 },
//             };
//         case 'UPDATE_CONTACT_NAME':
//             return {
//                 ...state,
//                 ...{
//                     contacts: state.contacts.map(itm => {
//                         if (itm.contactId === action.contactId) {
//                             itm = {
//                                 ...itm,
//                                 firstName: action.firstName,
//                                 lastName: action.lastName,
//                             };
//                         }
//                         return itm;
//                     }),
//                 },
//             };
//         case 'CLEAR_SELECTED_GRID_CONTACTS':
//             return {
//                 ...state,
//                 ...{
//                     selectAll: false,
//                     selected: [],
//                 },
//             };
//         default:
//             return state;
//     }
// };
